/**
 * city/textures/textures-surfaces.js
 * Texture bề mặt kiến trúc đô thị Việt Nam (vôi vữa, bê tông, ngói, tôn, cửa, gỗ, đá)
 */

import { createRng, hashString, shade } from '../builder-core.js';

class MethodsHolder {
  // Tường vữa sơn nước: hạt vữa, mảng loang, vệt nước mưa chảy dọc & chân tường ám bẩn
  getPlasterTexture(baseColor = '#f2c14e', seed = 1, aged = 0.5) {
    return this._tex(`plaster|${baseColor}|${seed}|${aged}`, 256, 256, (ctx, w, h) => {
      const rnd = createRng(seed);
      ctx.fillStyle = baseColor;
      ctx.fillRect(0, 0, w, h);

      for (let i = 0; i < 2600; i++) {
        const s = 1 + rnd() * 2;
        ctx.fillStyle =
          rnd() < 0.5 ? `rgba(255,255,255,${0.04 + rnd() * 0.07})` : `rgba(60,40,20,${0.03 + rnd() * 0.06})`;
        ctx.fillRect(rnd() * w, rnd() * h, s, s);
      }

      for (let i = 0; i < 5; i++) {
        const x = rnd() * w,
          y = rnd() * h,
          r = 30 + rnd() * 50;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(120,80,30,${0.07 * aged})`);
        g.addColorStop(1, 'rgba(120,80,30,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }

      for (let i = 0; i < Math.round(12 * aged); i++) {
        const x = rnd() * w,
          len = 40 + rnd() * 120,
          y0 = rnd() * h * 0.5;
        const g = ctx.createLinearGradient(0, y0, 0, y0 + len);
        g.addColorStop(0, `rgba(70,60,50,${0.1 * aged + 0.03})`);
        g.addColorStop(1, 'rgba(70,60,50,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x, y0, 2 + rnd() * 4, len);
      }

      const gb = ctx.createLinearGradient(0, h * 0.82, 0, h);
      gb.addColorStop(0, 'rgba(40,50,30,0)');
      gb.addColorStop(1, `rgba(40,50,30,${0.4 * aged})`);
      ctx.fillStyle = gb;
      ctx.fillRect(0, h * 0.82, w, h * 0.18);
    });
  }

  // Giữ tương thích API cũ: tường vôi vàng rêu phong phố cổ
  getMossyWallTexture(baseColor = '#facc15') {
    return this.getPlasterTexture(baseColor, hashString(baseColor), 0.9);
  }

  // Tường hông nhà ống để trần xi măng xám (đặc trưng phố Việt Nam)
  getConcreteTexture(seed = 1) {
    return this._tex(`concrete|${seed}`, 256, 256, (ctx, w, h) => {
      const rnd = createRng(seed * 31 + 7);
      ctx.fillStyle = '#a8a29e';
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 3000; i++) {
        const s = 1 + rnd() * 2;
        ctx.fillStyle = rnd() < 0.5 ? 'rgba(255,255,255,0.08)' : 'rgba(30,30,30,0.08)';
        ctx.fillRect(rnd() * w, rnd() * h, s, s);
      }
      // Mảng trát vá & vệt ố
      for (let i = 0; i < 6; i++) {
        ctx.fillStyle = `rgba(${rnd() < 0.5 ? '87,83,78' : '214,211,209'},${0.15 + rnd() * 0.15})`;
        ctx.fillRect(rnd() * w, rnd() * h, 20 + rnd() * 60, 14 + rnd() * 40);
      }
      for (let i = 0; i < 10; i++) {
        const x = rnd() * w,
          len = 60 + rnd() * 150;
        const g = ctx.createLinearGradient(0, 0, 0, len);
        g.addColorStop(0, 'rgba(41,37,36,0.22)');
        g.addColorStop(1, 'rgba(41,37,36,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x, 0, 3 + rnd() * 5, len);
      }
    });
  }

  // Cửa cuốn kim loại dập sóng ngang
  getRollerDoorTexture() {
    return this._tex('roller', 128, 128, (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#a1a1aa');
      g.addColorStop(1, '#71717a');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      for (let y = 0; y < h; y += 8) {
        ctx.fillStyle = 'rgba(255,255,255,0.28)';
        ctx.fillRect(0, y, w, 2);
        ctx.fillStyle = 'rgba(24,24,27,0.35)';
        ctx.fillRect(0, y + 6, w, 2);
      }
    });
  }

  // Mái ngói đất nung âm dương (Hội An): rãnh ngửa tối xen hàng úp sáng
  getRoofTileTexture() {
    return this._tex('rooftile', 256, 256, (ctx, w, h) => {
      const rnd = createRng(99);
      ctx.fillStyle = '#7c2d12';
      ctx.fillRect(0, 0, w, h);
      const col = 32,
        row = 24;
      for (let y = 0; y < h; y += row) {
        for (let x = 0; x < w; x += col) {
          const base = ['#b45309', '#c2410c', '#9a3412', '#a8431a'][Math.floor(rnd() * 4)];
          const g = ctx.createLinearGradient(x, 0, x + col / 2, 0);
          g.addColorStop(0, shade(base, -0.3));
          g.addColorStop(0.5, shade(base, 0.2));
          g.addColorStop(1, shade(base, -0.3));
          ctx.fillStyle = g;
          ctx.fillRect(x, y, col / 2, row);
          ctx.fillStyle = shade(base, -0.45);
          ctx.fillRect(x + col / 2, y, col / 2, row);
          if (rnd() < 0.12) {
            ctx.fillStyle = 'rgba(21,94,52,0.35)';
            ctx.fillRect(x, y + row * 0.5, col, row * 0.5);
          }
        }
        ctx.fillStyle = 'rgba(40,10,5,0.55)';
        ctx.fillRect(0, y + row - 3, w, 3);
      }
    });
  }

  // Mái tôn sóng
  getToleTexture(color = '#2563eb') {
    return this._tex(`tole|${color}`, 128, 128, (ctx, w, h) => {
      for (let x = 0; x < w; x += 16) {
        const g = ctx.createLinearGradient(x, 0, x + 16, 0);
        g.addColorStop(0, shade(color, -0.3));
        g.addColorStop(0.5, shade(color, 0.25));
        g.addColorStop(1, shade(color, -0.3));
        ctx.fillStyle = g;
        ctx.fillRect(x, 0, 16, h);
      }
      ctx.fillStyle = 'rgba(120,53,15,0.18)';
      ctx.fillRect(0, h * 0.85, w, h * 0.15);
    });
  }

  // Kính cửa: 'sky' phản chiếu trời xanh, 'home' nhìn vào phòng có rèm
  getWindowTexture(kind = 'sky') {
    return this._tex(`window|${kind}`, 128, 128, (ctx, w, h) => {
      if (kind === 'home') {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, '#475569');
        g.addColorStop(1, '#1e293b');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#fde68a';
        ctx.fillRect(0, 0, w * 0.26, h);
        ctx.fillRect(w * 0.74, 0, w * 0.26, h);
        ctx.fillStyle = 'rgba(180,120,40,0.35)';
        for (let x = 6; x < w * 0.26; x += 9) ctx.fillRect(x, 0, 3, h);
        for (let x = w * 0.74 + 6; x < w; x += 9) ctx.fillRect(x, 0, 3, h);
        const lamp = ctx.createRadialGradient(w * 0.5, h * 0.3, 2, w * 0.5, h * 0.3, 30);
        lamp.addColorStop(0, 'rgba(254,243,199,0.8)');
        lamp.addColorStop(1, 'rgba(254,243,199,0)');
        ctx.fillStyle = lamp;
        ctx.fillRect(0, 0, w, h);
      } else {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, '#dbeafe');
        g.addColorStop(0.45, '#7cb4e6');
        g.addColorStop(1, '#27496d');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      ctx.beginPath();
      ctx.moveTo(w * 0.15, 0);
      ctx.lineTo(w * 0.5, 0);
      ctx.lineTo(0, h * 0.8);
      ctx.lineTo(0, h * 0.35);
      ctx.closePath();
      ctx.fill();
    });
  }

  // Cửa chớp lá sách (louver)
  getLouverTexture(color = '#2f6b3a') {
    return this._tex(`louver|${color}`, 64, 128, (ctx, w, h) => {
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, w, h);
      for (let y = 8; y < h - 8; y += 7) {
        ctx.fillStyle = shade(color, 0.25);
        ctx.fillRect(6, y, w - 12, 2);
        ctx.fillStyle = shade(color, -0.4);
        ctx.fillRect(6, y + 4, w - 12, 2);
      }
      ctx.strokeStyle = shade(color, -0.35);
      ctx.lineWidth = 5;
      ctx.strokeRect(2, 2, w - 4, h - 4);
      ctx.beginPath();
      ctx.moveTo(0, h / 2);
      ctx.lineTo(w, h / 2);
      ctx.stroke();
    });
  }

  // Ván gỗ ghép (cửa bức bàn, cánh cửa gỗ)
  getWoodPanelTexture(color = '#7c2d12') {
    return this._tex(`woodpanel|${color}`, 128, 256, (ctx, w, h) => {
      const rnd = createRng(hashString(color));
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 60; i++) {
        ctx.fillStyle = `rgba(0,0,0,${0.05 + rnd() * 0.08})`;
        ctx.fillRect(rnd() * w, 0, 1, h);
      }
      ctx.strokeStyle = shade(color, -0.4);
      ctx.lineWidth = 4;
      ctx.strokeRect(14, 14, w - 28, h * 0.42);
      ctx.strokeRect(14, h * 0.52, w - 28, h * 0.42);
      ctx.strokeStyle = shade(color, 0.2);
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, w - 40, h * 0.42 - 12);
      ctx.strokeRect(20, h * 0.52 + 6, w - 40, h * 0.42 - 12);
    });
  }

  // Ốp đá tự nhiên (biệt thự, tường rào)
  getStoneTexture() {
    return this._tex('stone', 256, 256, (ctx, w, h) => {
      const rnd = createRng(17);
      ctx.fillStyle = '#44403c';
      ctx.fillRect(0, 0, w, h);
      let y = 0;
      while (y < h) {
        const rowH = 22 + rnd() * 18;
        let x = -rnd() * 40;
        while (x < w) {
          const bw = 40 + rnd() * 50;
          ctx.fillStyle = ['#a8a29e', '#8c847c', '#b5aa9c', '#78716c', '#9c9188'][Math.floor(rnd() * 5)];
          ctx.fillRect(x + 2, y + 2, bw - 4, rowH - 4);
          ctx.fillStyle = 'rgba(255,255,255,0.12)';
          ctx.fillRect(x + 2, y + 2, bw - 4, 3);
          x += bw;
        }
        y += rowH;
      }
    });
  }
}

export function registerTextureSurfacesMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
